import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { InfoBox, SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  h2: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 36, marginBottom: 14, letterSpacing: '-0.2px' },
  h3: { fontSize: 17, fontWeight: 600, color: '#1e293b', marginTop: 24, marginBottom: 10 },
  p: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 16 },
  ul: { paddingLeft: 20, marginBottom: 16 },
  li: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 6 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14 },
  th: { background: '#f8fafc', padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#374151', verticalAlign: 'top' },
  tdAlt: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#374151', verticalAlign: 'top', background: '#f8fafc' },
  highlight: { color: '#1e40af', fontWeight: 600 },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  prevLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
  nextLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

export default function EURules() {
  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; EU procurement rules
      </div>

      <h1 style={s.h1}>EU procurement rules explained</h1>
      <p style={s.lead}>
        The EU has a detailed legal framework governing how public contracts must be procured. Understanding the key rules — particularly the thresholds that trigger EU-wide publication — helps you know which contracts you can see, and which may only be advertised locally.
      </p>

      <h2 style={s.h2}>Why EU rules exist</h2>
      <p style={s.p}>
        The EU single market principle requires that public contracts above certain values be open to competition from any business in the EU. Without common rules, governments would favour domestic suppliers, fragmenting the market and raising costs for taxpayers. The EU Procurement Directives (mainly 2014/24/EU for public contracts and 2014/25/EU for utilities) harmonise these rules across all member states.
      </p>
      <p style={s.p}>
        Member states must implement these directives in national law. The rules set <em>minimum</em> standards — national governments can be more transparent, but not less.
      </p>

      <h2 style={s.h2}>The thresholds that trigger EU-wide publication</h2>
      <p style={s.p}>
        Contracts above these values must be published EU-wide and follow the full EU procedure. Below these thresholds, member states set their own rules (though many still require some form of competitive procurement).
      </p>
      <p style={s.p}>Thresholds are revised every two years. Current values (2024&ndash;2025):</p>

      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Contract type</th>
              <th style={s.th}>Central government</th>
              <th style={s.th}>Other public bodies</th>
              <th style={s.th}>Utilities</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={s.td}>Goods (supplies)</td>
              <td style={{ ...s.td, ...s.highlight }}>&euro;143,000</td>
              <td style={s.td}>&euro;221,000</td>
              <td style={s.td}>&euro;443,000</td>
            </tr>
            <tr>
              <td style={s.tdAlt}>Services</td>
              <td style={{ ...s.tdAlt, ...s.highlight }}>&euro;143,000</td>
              <td style={s.tdAlt}>&euro;221,000</td>
              <td style={s.tdAlt}>&euro;443,000</td>
            </tr>
            <tr>
              <td style={s.td}>Works (construction)</td>
              <td style={{ ...s.td, ...s.highlight }}>&euro;5,538,000</td>
              <td style={s.td}>&euro;5,538,000</td>
              <td style={s.td}>&euro;5,538,000</td>
            </tr>
            <tr>
              <td style={s.tdAlt}>Light-touch services*</td>
              <td style={s.tdAlt} colSpan={3}>&euro;750,000 (all authorities)</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ ...s.p, fontSize: 13, color: '#94a3b8' }}>* Light-touch services include social, health, education, and certain legal services — subject to a simplified regime.</p>

      <InfoBox>
        <strong>What this means in practice:</strong> A &euro;300,000 IT services contract with a central government ministry must be published EU-wide and visible to any EU business. The same contract with a municipal authority would also cross the &euro;221,000 threshold. Below-threshold contracts may only appear on national or local portals — or not be publicly advertised at all.
      </InfoBox>

      <h2 style={s.h2}>The three main EU directives</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Directive 2014/24/EU</strong> — the main public procurement directive, covering government ministries, agencies, regional authorities, and most other public bodies</li>
        <li style={s.li}><strong>Directive 2014/25/EU</strong> — covers utilities: water, energy, transport, and postal services operators. Higher thresholds and somewhat more flexible procedures</li>
        <li style={s.li}><strong>Directive 2014/23/EU</strong> — specifically governs concession contracts (long-term rights to operate a service, where the operator takes the demand risk)</li>
      </ul>

      <h2 style={s.h2}>Framework agreements</h2>
      <p style={s.p}>
        A framework agreement is a master contract between a contracting authority and one or more suppliers, establishing the terms under which individual contracts ("call-offs") can be placed without running a full tender each time.
      </p>
      <p style={s.p}>
        Frameworks typically last up to four years. Being on a framework does not guarantee revenue — it means you are pre-qualified and eligible for call-offs. Some frameworks require a further mini-competition among panel members; others allow direct call-off at pre-agreed rates.
      </p>
      <p style={s.p}>
        For suppliers, frameworks are particularly valuable because they generate recurring revenue with lower bid cost per contract. Winning a place on a major government IT or professional services framework can sustain a company for years.
      </p>

      <h2 style={s.h2}>Dynamic Purchasing Systems (DPS)</h2>
      <p style={s.p}>
        A DPS is similar to a framework but open-ended: new suppliers can join at any time during its life (which can be indefinite). Buyers run mini-competitions among DPS members for individual contracts. The DPS model is increasingly common for categories like temporary staffing, digital services, and consultancy.
      </p>

      <h2 style={s.h2}>The standstill period and your right to challenge</h2>
      <p style={s.p}>
        After a contract award decision is notified, EU law requires a mandatory standstill period of at least 10 calendar days before the contract can be signed. This pause gives unsuccessful bidders time to review the decision and lodge a legal challenge if they believe the procurement was flawed.
      </p>
      <p style={s.p}>
        If you receive an award notification and believe the scoring was incorrect or the process was unfair, you should act quickly. Remedies — which can include suspension of the contract or, rarely, contract set-aside — are time-limited.
      </p>

      <h2 style={s.h2}>Post-Brexit: UK and EU procurement</h2>
      <p style={s.p}>
        Since 2021, the UK is no longer part of the EU procurement regime. UK companies no longer have automatic rights to bid for EU contracts, and EU companies no longer have automatic rights to bid for UK contracts. Both markets remain accessible in practice — particularly for higher-value contracts — but there is no longer a legal obligation on either side to provide equal treatment.
      </p>
      <p style={s.p}>
        The UK has implemented its own Procurement Act 2023, which replaces the previous EU-derived regulations with a simplified domestic regime.
      </p>

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/winning-a-bid" style={s.prevLink}>&larr; What makes a winning bid?</Link>
        <Link to="/guide/glossary" style={s.nextLink}>Next: Glossary &rarr;</Link>
      </div>
    </GuideLayout>
  );
}
